import { Link, useLocation } from 'react-router-dom';
import {
  Briefcase,
  LayoutDashboard,
  MessageCircle,
  MoreHorizontal,
  Users,
  type LucideIcon,
} from 'lucide-react';

type PortalBottomTabsProps = {
  menuOpen: boolean;
  onMore: () => void;
  unreadCount?: number;
};

type Tab = {
  label: string;
  path: string;
  icon: LucideIcon;
};

const tabs: Tab[] = [
  { label: 'Home', path: '/agency/dashboard', icon: LayoutDashboard },
  { label: 'Campaigns', path: '/agency/campaigns', icon: Briefcase },
  { label: 'KOLs', path: '/agency/kols', icon: Users },
  { label: 'Inbox', path: '/agency/inbox', icon: MessageCircle },
];

function matchesPath(pathname: string, path: string) {
  return pathname === path || pathname.startsWith(`${path}/`);
}

const baseClass =
  'relative flex min-h-11 min-w-0 flex-1 touch-manipulation flex-col items-center justify-center gap-0.5 px-1 text-[10px] leading-none transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent2/60';

export function PortalBottomTabs({
  menuOpen,
  onMore,
  unreadCount = 0,
}: PortalBottomTabsProps) {
  const { pathname } = useLocation();
  const hasCoreMatch = tabs.some((tab) => matchesPath(pathname, tab.path));
  const moreActive = menuOpen || !hasCoreMatch;

  return (
    <nav
      className="fixed inset-x-0 bottom-0 z-40 flex h-[calc(56px+env(safe-area-inset-bottom))] items-start border-t border-glass bg-glass-3 pb-[env(safe-area-inset-bottom)] backdrop-blur-[28px] md:hidden"
      aria-label="Agency shortcuts"
    >
      {tabs.map((tab) => {
        const Icon = tab.icon;
        const active = matchesPath(pathname, tab.path);
        return (
          <Link
            key={tab.path}
            to={tab.path}
            className={`${baseClass} h-14 ${
              active
                ? 'font-bold text-accentText'
                : 'font-medium text-glass-text-3 hover:text-glass-text-2'
            }`}
            aria-current={active ? 'page' : undefined}
          >
            <span className="relative">
              <Icon className="h-[21px] w-[21px]" aria-hidden />
              {tab.label === 'Inbox' && unreadCount > 0 ? (
                <span className="absolute -right-2.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent2 px-0.5 text-[9px] font-bold text-white shadow-[0_0_8px_rgb(var(--accent-rgb)/0.5)]">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              ) : null}
            </span>
            <span>{tab.label}</span>
          </Link>
        );
      })}

      <button
        type="button"
        onClick={onMore}
        className={`${baseClass} h-14 ${
          moreActive
            ? 'font-bold text-accentText'
            : 'font-medium text-glass-text-3 hover:text-glass-text-2'
        }`}
        aria-label={menuOpen ? 'Close more navigation' : 'Open more navigation'}
        aria-expanded={menuOpen}
      >
        <MoreHorizontal className="h-[21px] w-[21px]" aria-hidden />
        <span>More</span>
      </button>
    </nav>
  );
}
